// ADR-119: selector for Organizational Structure Types — the classification of
// Clusters. Structural clone of OrgUnitTypeSelector, kept separate so
// the two taxonomies can evolve independently. Supports single and multiple
// selection; `multiple` renders chips and can offer a "No type" option
// (NO_TYPE_VALUE) so unclassified Clusters are filterable too.
import React, { useState, useEffect } from 'react';
import { FormControl, InputLabel, Select, MenuItem, CircularProgress, Box, Chip, OutlinedInput } from '@mui/material';
import { organizationApi } from '../../../services/api';
import { useAlert } from '../../../contexts/AlertContext';
import { ORG_STRUCTURE_TYPE_RETURN_TYPES } from '../../../constants/selectorReturnTypes';
import { getClusterChipStyle } from '../../../utils/clusterUtils';

/** Sentinel for "Clusters with no type" in multiple mode. */
export const NO_TYPE_VALUE = '__no_type__';

const OrgStructureTypeSelector = ({
  value,
  onChange,
  label = "Organizational Structure Type",
  required = false,
  disabled = false,
  fullWidth = true,
  size = 'medium',
  returnValue = ORG_STRUCTURE_TYPE_RETURN_TYPES.ID, // ID | NAME | CODE (single mode only)
  showEmptyOption = true,
  emptyOptionLabel = "None",
  helperText = '',
  multiple = false,
  showNoTypeOption = false, // multiple mode: offer NO_TYPE_VALUE
  noTypeOptionLabel = 'No type',
  sx = {},
  ...props
}) => {
  const [orgStructureTypes, setOrgStructureTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { showAlert } = useAlert();

  useEffect(() => {
    const fetchOrgStructureTypes = async () => {
      try {
        setIsLoading(true);
        const data = await organizationApi.getOrgStructureTypes();
        setOrgStructureTypes(data);
      } catch (error) {
        showAlert('Failed to load organizational structure types', 'error', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchOrgStructureTypes();
  }, [showAlert]);

  const handleChange = (event) => {
    if (!onChange) return;
    const selected = event.target.value;

    if (multiple) {
      onChange(Array.isArray(selected) ? selected : []);
      return;
    }

    // Empty option: hand back null so callers can clear the FK explicitly
    if (selected === '') {
      onChange(null);
      return;
    }
    const type = orgStructureTypes.find(t => t.id === selected);
    if (returnValue === ORG_STRUCTURE_TYPE_RETURN_TYPES.NAME) {
      onChange(type?.name || '');
    } else if (returnValue === ORG_STRUCTURE_TYPE_RETURN_TYPES.CODE) {
      onChange(type?.code || '');
    } else {
      onChange(selected);
    }
  };

  const selectedSingle = !multiple
    ? (orgStructureTypes.find(type => {
        if (returnValue === ORG_STRUCTURE_TYPE_RETURN_TYPES.NAME) return type.name === value;
        if (returnValue === ORG_STRUCTURE_TYPE_RETURN_TYPES.CODE) return type.code === value;
        return type.id === value;
      }) || null)
    : null;

  const multiValue = multiple ? (Array.isArray(value) ? value : []) : undefined;

  const labelFor = (id) => (id === NO_TYPE_VALUE
    ? noTypeOptionLabel
    : (orgStructureTypes.find(t => t.id === id)?.name || id));

  // Same pattern as ClusterSelector's multiple mode: chips once something is
  // selected; while empty the floating label stays inside the field (no
  // placeholder, so nothing to overlap with).
  const renderMultiValue = (selected) => {
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {selected.map((id) => (
          <Chip key={id} label={labelFor(id)} size="small" sx={getClusterChipStyle()} />
        ))}
      </Box>
    );
  };

  return (
    <FormControl fullWidth={fullWidth} required={required} disabled={disabled || isLoading} size={size} sx={sx} {...props}>
      <InputLabel>{label}</InputLabel>
      <Select
        multiple={multiple}
        value={multiple ? multiValue : (selectedSingle?.id || '')}
        onChange={handleChange}
        label={label}
        input={multiple ? <OutlinedInput label={label} /> : undefined}
        renderValue={multiple ? renderMultiValue : undefined}
        disabled={disabled || isLoading}
        endAdornment={
          isLoading ? <CircularProgress color="inherit" size={20} /> : null
        }
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: 300,
              zIndex: 1350
            }
          }
        }}
      >
        {!multiple && !required && showEmptyOption && (
          <MenuItem value="">
            <em>{emptyOptionLabel}</em>
          </MenuItem>
        )}

        {multiple && showNoTypeOption && (
          <MenuItem value={NO_TYPE_VALUE}>
            <em>{noTypeOptionLabel}</em>
          </MenuItem>
        )}

        {orgStructureTypes.map(type => (
          <MenuItem key={type.id} value={type.id}>
            {type.name}
            {type.code && (
              <span style={{ marginLeft: 'auto', paddingLeft: 16, color: '#6B7280', fontSize: '0.75rem', fontFamily: 'monospace' }}>
                {type.code}
              </span>
            )}
          </MenuItem>
        ))}
      </Select>
      {helperText && (
        <span style={{ marginTop: 4, marginLeft: 14, fontSize: '0.75rem', color: '#6B7280' }}>{helperText}</span>
      )}
    </FormControl>
  );
};

export default OrgStructureTypeSelector;
